import type { ZoomRange } from "./trendingUrlState";

export type TimeWindow = {
	start: number;
	end: number;
};

export type RawModeState = {
	isArmed: boolean;
	isActive: boolean;
	window: TimeWindow;
};

export const RAW_MODE_MAX_TOTAL_COUNT = 100000;

export const RAW_MODE_OFF: RawModeState = {
	isArmed: false,
	isActive: false,
	window: { start: 0, end: 0 },
};

export const calculateVisibleWindow = (
	timeScopeStart: Date,
	timeScopeEnd: Date,
	zoom: ZoomRange | null,
): TimeWindow => {
	const start = timeScopeStart.getTime();
	const end = timeScopeEnd.getTime();

	if (!zoom) {
		return { start, end };
	}

	const span = end - start;
	return {
		start: Math.round(start + (span * zoom.start) / 100),
		end: Math.round(start + (span * zoom.end) / 100),
	};
};

export const getWindowSpan = (window: TimeWindow) => window.end - window.start;

export const isSameWindow = (a: TimeWindow, b: TimeWindow) =>
	a.start === b.start && a.end === b.end;

export const isWindowContained = (inner: TimeWindow, outer: TimeWindow) =>
	inner.start >= outer.start && inner.end <= outer.end;

export const sumTotalValueCount = (rawCounts: number[]) =>
	rawCounts.reduce((total, count) => total + count, 0);

export const buildRawModeResetKey = (
	timeScopeStart: Date,
	timeScopeEnd: Date,
	tagIds: number[],
) => `${timeScopeStart.getTime()}-${timeScopeEnd.getTime()}-${tagIds.join(",")}`;

export const armRawMode = (
	previous: RawModeState,
	fetchedWindow: TimeWindow,
	windowTotalCount: number,
): RawModeState => {
	if (previous.isActive) return previous;

	const isArmed = windowTotalCount <= RAW_MODE_MAX_TOTAL_COUNT;
	if (
		previous.isArmed === isArmed &&
		isSameWindow(previous.window, fetchedWindow)
	) {
		return previous;
	}

	return { isArmed, isActive: false, window: fetchedWindow };
};

export const resolveRawMode = (
	previous: RawModeState,
	visibleWindow: TimeWindow,
	previousVisibleSpan: number | null,
): RawModeState => {
	if (previous.isActive) {
		return isWindowContained(visibleWindow, previous.window)
			? previous
			: RAW_MODE_OFF;
	}

	if (!previous.isArmed) return previous;

	const hasZoomedIn =
		previousVisibleSpan !== null &&
		getWindowSpan(visibleWindow) < previousVisibleSpan;

	return hasZoomedIn
		? { isArmed: false, isActive: true, window: previous.window }
		: previous;
};
